import React, { useEffect, useRef } from "react";
import shaka from "shaka-player/dist/shaka-player.ui";
import {
  getShakaAudioLiveMedia,
  setShakaAudioLiveMedia,
  useCardAudioShakaMiniPlayerStore,
} from "@/cards/common/component/background-players/cardAudioShakaMiniPlayerStore";
import {
  dismissCardVideoMiniPlayer,
  pauseOtherCardVideos,
  pauseAllCardYoutubeEmbeds,
} from "@/cards/common/component/background-players/singleCardMedia";
import { pauseActiveCardAudio } from "@/cards/common/component/background-players/singleCardAudio";

export const TAAS_SHAKA_AUDIO_ATTR = "data-taas-card-shaka-audio";

type ShakaPlayerAudioProps = {
  src: string;
  /** When this number changes, call play() (same track resume / re-play). */
  playRequestId?: number;
};

type ShakaPlayerInstance = InstanceType<typeof shaka.Player>;

/**
 * Persistent Shaka audio engine (any format Shaka can play).
 * Mounted inside CardAudioMiniPlayer so page changes do NOT restart playback.
 * Only remounts when `src` changes (user played a different track).
 */
const ShakaPlayerAudio = ({ src, playRequestId = 0 }: ShakaPlayerAudioProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<ShakaPlayerInstance | null>(null);
  const loadedSrcRef = useRef("");

  // Create player + load URL when src changes.
  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    if (!video) return;

    video.setAttribute(TAAS_SHAKA_AUDIO_ATTR, "1");
    setShakaAudioLiveMedia(video);

    const start = async () => {
      shaka.polyfill.installAll();

      if (!shaka.Player.isBrowserSupported()) {
        console.error("Shaka Player is not supported");
        return;
      }

      const player = new shaka.Player(video);
      playerRef.current = player;

      player.addEventListener("error", (event: Event) => {
        const errorEvent = event as Event & { detail?: unknown };
        console.error("Shaka audio error:", errorEvent.detail);
      });

      try {
        await player.load(src);
        if (cancelled || getShakaAudioLiveMedia() !== video) return;
        loadedSrcRef.current = src;

        pauseEveryoneElse(video);
        if (cancelled || getShakaAudioLiveMedia() !== video) return;
        await video.play().catch(() => {});
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load audio:", error);
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (playerRef.current) {
        void playerRef.current.destroy();
        playerRef.current = null;
      }
      loadedSrcRef.current = "";
      setShakaAudioLiveMedia(null);
    };
  }, [src]);

  // Same track: user pressed play again → just play(), do not reload.
  useEffect(() => {
    if (!playRequestId) return;
    const video = videoRef.current;
    if (!video) return;
    if (loadedSrcRef.current !== src) return;
    if (getShakaAudioLiveMedia() !== video) return;

    pauseEveryoneElse(video);
    if (getShakaAudioLiveMedia() !== video) return;
    void video.play().catch(() => {});
  }, [playRequestId, src]);

  // Sync media events → store (drives page UI + mini UI).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      if (getShakaAudioLiveMedia() !== video) return;
      pauseEveryoneElse(video);
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({
        playing: true,
        currentTime: video.currentTime || 0,
      });
    };

    const onPause = () => {
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({
        playing: false,
        currentTime: video.currentTime || 0,
      });
    };

    const onEnded = () => {
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({
        playing: false,
        currentTime: 0,
      });
    };

    const onTime = () => {
      const time = video.currentTime || 0;
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({
        currentTime: time,
        duration,
      });
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("loadedmetadata", onTime);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("loadedmetadata", onTime);
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      playsInline
      controls={false}
      disablePictureInPicture
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    />
  );
};

export function pauseEveryoneElse(exceptVideo: HTMLVideoElement) {
  // Ignore stale play() callbacks after the audio engine was dismissed —
  // those were resetting / tearing down the video mini-player on navigate.
  if (getShakaAudioLiveMedia() !== exceptVideo) return;

  dismissCardVideoMiniPlayer();
  pauseOtherCardVideos(exceptVideo);
  pauseAllCardYoutubeEmbeds();
  pauseActiveCardAudio();
}

export async function playShakaAudioExclusive(media: HTMLVideoElement): Promise<void> {
  pauseEveryoneElse(media);
  await media.play();
}

export default ShakaPlayerAudio;
